// src/services/token.service.ts
import { PaymentStatus } from '../types/payment.types';
import { ApiService } from './api.service';

export interface TokenPackage {
  _id: string;
  name: string;
  tokenAmount: number;
  price: number;
  isMonthly: boolean;
  discountPercent: number;
  description: string;
  monthlyRenewal: boolean;
}

export interface UserToken {
  balance: number;
  totalPurchased: number;
  totalUsed: number;
  expiryDate: string | null;
  subscription?: {
    packageInfo: {
      name: string;
      tokenAmount: number;
      isMonthly: boolean;
      monthlyRenewal: boolean;
    };
    startDate: string;
    nextRenewalDate: string;
    lastRenewalDate: string;
  };
}

export interface PaymentResponse {
  paymentUrl: string;
  qrCode: string;
  amount: number;
  expiryTime: number;
  orderCode: string;
}

export interface PaymentDetails {
  paymentId: string;
  amount: number;
  status: PaymentStatus;
  statusDetail: string;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
  package: {
    id: string;
    name: string;
    tokenAmount: number;
    isMonthly: boolean;
    price: number;
  };
}

export interface Transaction {
  id: string;
  paymentId: string;
  amount: number;
  status: PaymentStatus;
  statusDetail: string;
  paymentMethod: string;
  date: string;
  package: {
    name: string;
    tokenAmount: number;
    isMonthly: boolean;
    price: number;
  } | null;
}

export interface TransactionResponse {
  transactions: Transaction[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface AdminTransaction extends Transaction {
  userId: string;
  userName: string;
  userEmail: string;
}

export interface AdminTransactionResponse {
  transactions: AdminTransaction[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export class TokenService {
  static async getTokenPackages(): Promise<TokenPackage[]> {
    const response = await ApiService.get<{ success: boolean; data: TokenPackage[] }>('/tokens/packages');
    return response.data;
  }

  static async getUserToken(): Promise<UserToken> {
    const response = await ApiService.get<{ success: boolean; data: UserToken }>('/tokens/balance');
    return response.data;
  }

  static async createPayment(packageId: string): Promise<PaymentResponse> {
    const response = await ApiService.post<{ success: boolean; data: PaymentResponse }>('/tokens/payment', { packageId });
    return response.data;
  }

  static async getPaymentDetails(paymentId: string): Promise<PaymentDetails> {
    const response = await ApiService.get<{ success: boolean; data: PaymentDetails }>(`/tokens/payment/${paymentId}`);
    return response.data;
  }

  static async getTransactions(page: number = 1, limit: number = 10): Promise<TransactionResponse> {
    const response = await ApiService.get<{ success: boolean; data: TransactionResponse }>(`/tokens/transactions?page=${page}&limit=${limit}`);
    return response.data;
  }
  
  static async updatePaymentStatus(paymentId: string, status: PaymentStatus): Promise<any> {
    const response = await ApiService.post<{ success: boolean; data: any }>(
      `/tokens/payment/${paymentId}/update-status`, 
      { status }
    );
    return response.data;
  }

  static async useTokens(tokenAmount: number, text: string, characterName: string): Promise<UserToken> {
    const response = await ApiService.post<{ success: boolean; data: UserToken }>('/tokens/use', { 
      tokenAmount, 
      text,
      characterName
    });
    return response.data;
  }

  static async getAllTransactions(
    page: number = 1, 
    limit: number = 10, 
    filters: { status?: string; userId?: string; dateFrom?: string; dateTo?: string } = {}
  ): Promise<AdminTransactionResponse> {
    let queryParams = `page=${page}&limit=${limit}`;
    
    // Add filters to query params
    if (filters.status) queryParams += `&status=${filters.status}`;
    if (filters.userId) queryParams += `&userId=${filters.userId}`;
    if (filters.dateFrom) queryParams += `&dateFrom=${filters.dateFrom}`;
    if (filters.dateTo) queryParams += `&dateTo=${filters.dateTo}`;
    
    const response = await ApiService.get<{ success: boolean; data: AdminTransactionResponse }>(
      `/tokens/admin/transactions?${queryParams}`
    );
    return response.data;
  }
}